export function chooseAdditionalHandles(currentHandle, existingHandles, candidateHandles, limit) {
  const seen = new Set([currentHandle, ...existingHandles]);
  const chosen = [];
  for (const handle of candidateHandles) {
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    chosen.push(handle);
    if (chosen.length + existingHandles.length >= limit) break;
  }
  return chosen;
}

export function buildCollectionFeedUrl(collectionUrl, filters, origin, pageUrl) {
  const url = new URL(pageUrl || collectionUrl, origin);
  url.searchParams.delete('filter.p.m.custom.styles');
  url.searchParams.delete('filter.p.product_type');
  url.searchParams.delete('filter.v.availability');
  for (const style of filters.styles || []) url.searchParams.append('filter.p.m.custom.styles', style);
  if (filters.productType) url.searchParams.set('filter.p.product_type', filters.productType);
  if (filters.availability !== undefined) url.searchParams.set('filter.v.availability', filters.availability);
  url.searchParams.set('section_id', 'main-collection');
  return url.toString();
}

export function nextCarouselStart(currentStart, direction) {
  return Math.max(0, currentStart + direction * 8);
}

function initSimilar(section) {
  if (section.dataset.similarBound === 'true') return;
  section.dataset.similarBound = 'true';
  const scroller = section.querySelector('[data-similar-scroller]');
  const previous = section.querySelector('[data-similar-prev]');
  const next = section.querySelector('[data-similar-next]');
  const arrows = section.querySelector('[data-similar-arrows]');
  const content = section.querySelector('[data-similar-content]');
  const status = section.querySelector('[data-similar-status]');
  const limit = Number(section.dataset.limit) || 8;
  const cards = () => [...scroller.querySelectorAll('.listing-card')];
  const collectionUrl = section.dataset.subjectCollectionUrl;
  const styles = section.dataset.styleValues?.split('|').filter(Boolean) || [];
  const productType = section.dataset.productType;
  const passes = [
    ...(styles.length ? [{ styles, productType, availability: '1' }] : []),
    { productType, availability: '1' },
    ...(styles.length ? [{ styles, productType, availability: '0' }] : []),
    { productType, availability: '0' },
  ];
  let passIndex = 0;
  let nextPageUrl = null;
  let feedLoading = false;
  let feedExhausted = !collectionUrl;
  let activeLoad = null;
  let navigationBusy = false;
  const pendingCards = [];

  function loadMoreFromCollection() {
    if (activeLoad) return activeLoad;
    if (feedExhausted) return Promise.resolve(0);
    activeLoad = (async () => {
      feedLoading = true;
      section.dataset.feedLoading = 'true';
      status.textContent = 'Loading more pieces…';
      let appended = 0;
      const existing = new Set([section.dataset.productHandle, ...cards().map((card) => card.dataset.productHandle)]);
      try {
        while (appended < 8) {
          if (pendingCards.length) {
            const card = pendingCards.shift();
            const handle = card.dataset.productHandle;
            if (!handle || existing.has(handle)) continue;
            if (productType && card.querySelector('.listing-card__meta-row p')?.textContent.trim() !== productType) continue;
            existing.add(handle);
            const item = document.createElement('div');
            item.className = 'pdp-similar__item';
            item.append(card);
            scroller.append(item);
            appended += 1;
            continue;
          }
          if (passIndex >= passes.length) break;
          const requestUrl = buildCollectionFeedUrl(collectionUrl, passes[passIndex], window.location.origin, nextPageUrl);
          const response = await fetch(requestUrl);
          if (!response.ok) throw new Error(`Collection HTTP ${response.status}`);
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          pendingCards.push(...page.querySelectorAll('[data-listing-grid] .listing-card'));
          section.dataset.feedPagesLoaded = String(Number(section.dataset.feedPagesLoaded || 0) + 1);
          nextPageUrl = page.querySelector('[data-infinite-scroll]')?.dataset.nextUrl || null;
          if (!nextPageUrl) passIndex += 1;
        }
        if (appended) content.hidden = false;
        feedExhausted = passIndex >= passes.length && pendingCards.length === 0;
        section.dataset.feedExhausted = String(feedExhausted);
        status.textContent = '';
        return appended;
      } catch (error) {
        status.textContent = 'More pieces could not load. Scroll again to retry.';
        console.warn('Similar pieces could not load more collection products.', error);
        return appended;
      } finally {
        feedLoading = false;
        section.dataset.feedLoading = 'false';
        activeLoad = null;
        updateControls();
      }
    })();
    return activeLoad;
  }

  function maybeLoadMore() {
    if (feedLoading || feedExhausted || content.hidden) return;
    const distanceToEnd = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
    if (distanceToEnd < 420) loadMoreFromCollection();
  }

  function updateControls() {
    const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 2;
    arrows.hidden = cards().length === 0 || (!hasOverflow && feedExhausted);
    previous.disabled = scroller.scrollLeft <= 2;
    next.disabled = feedExhausted && scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
  }

  async function scrollByBatch(direction) {
    if (navigationBusy) return;
    navigationBusy = true;
    const item = scroller.querySelector('.pdp-similar__item');
    const gap = Number.parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const step = (item?.getBoundingClientRect().width || scroller.clientWidth / 4) + gap;
    const currentStart = Math.round(scroller.scrollLeft / step);
    const targetStart = nextCarouselStart(currentStart, direction);
    try {
      if (direction > 0) {
        while (cards().length <= targetStart && !feedExhausted) {
          const before = cards().length;
          await loadMoreFromCollection();
          if (cards().length === before) break;
        }
      }
      const visibleCount = Math.max(1, Math.floor((scroller.clientWidth + gap) / step));
      const lastStart = Math.max(0, cards().length - visibleCount);
      scroller.scrollTo({ left: Math.min(targetStart, lastStart) * step, behavior: 'smooth' });
    } finally {
      navigationBusy = false;
      updateControls();
    }
  }

  previous.addEventListener('click', () => scrollByBatch(-1));
  next.addEventListener('click', () => scrollByBatch(1));
  scroller.addEventListener('scroll', () => {
    updateControls();
    maybeLoadMore();
  }, { passive: true });
  window.addEventListener('resize', () => {
    updateControls();
    maybeLoadMore();
  }, { passive: true });
  updateControls();

  async function loadFallback() {
    if (section.dataset.fallbackRequested === 'true') return;
    section.dataset.fallbackRequested = 'true';
    const endpoint = new URL(section.dataset.recommendationsUrl, window.location.origin);
    endpoint.searchParams.set('product_id', section.dataset.productId);
    endpoint.searchParams.set('section_id', section.dataset.sectionId);
    endpoint.searchParams.set('intent', 'related');
    endpoint.searchParams.set('limit', '10');
    try {
      const response = await fetch(endpoint.toString());
      if (!response.ok) throw new Error(`Recommendations HTTP ${response.status}`);
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const candidates = [...documentFragment.querySelectorAll('[data-similar-fallback] .pdp-similar__item')];
      const byHandle = new Map(candidates.map((item) => [item.querySelector('.listing-card')?.dataset.productHandle, item]));
      const selected = chooseAdditionalHandles(
        section.dataset.productHandle,
        cards().map((card) => card.dataset.productHandle),
        [...byHandle.keys()],
        limit,
      );
      for (const handle of selected) scroller.append(byHandle.get(handle));
      if (cards().length) content.hidden = false;
      updateControls();
      maybeLoadMore();
    } catch (error) {
      console.warn('Similar pieces could not load related recommendations.', error);
    }
  }

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      if (cards().length < limit && section.dataset.needsFallback === 'true') loadFallback();
      maybeLoadMore();
    }, { rootMargin: '240px' });
    observer.observe(section);
  } else {
    if (cards().length < limit && section.dataset.needsFallback === 'true') loadFallback();
    maybeLoadMore();
  }
}

if (typeof document !== 'undefined') {
  const initAll = () => document.querySelectorAll('[data-similar-section]').forEach(initSimilar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
}
